/**
 * Headless input state. Consumed by movement per tick; produced by a DOM
 * binding at the edge of the app. No PixiJS, no DOM imports here.
 *
 * Keys are `KeyboardEvent.code` values (layout-independent).
 */

import type { Stance } from './player';

export interface InputState {
  /** Currently-held key codes. */
  held: Set<string>;
  /** Set on Backquote keydown; consumers call `consumeDebugToggle` to reset. */
  debugToggleRequested: boolean;
  /** Latched by C keypress; toggle behavior per CLAUDE.md movement spec. */
  crouchLatched: boolean;
  // Combat edge triggers (consumed each tick):
  focusToggleRequested: boolean;
  cyclePrevRequested: boolean;
  cycleNextRequested: boolean;
  /** Weapon slot request in [1..4], or 0 for "no request this tick". */
  weaponSwitchRequested: 0 | 1 | 2 | 3 | 4;
  /** M key: toggle marked on currently-focused guard. */
  markRequested: boolean;
  /** E key: interact — currently loot an adjacent corpse. */
  interactRequested: boolean;
  /** Q key: descend the stairs (E ascends). Only acts while standing on stairs. */
  descendRequested: boolean;
  /** B key: hurl a carried loose object along the player's facing (prop verbs). */
  throwRequested: boolean;
  /** U key: put on (or take off) a looted uniform. */
  uniformRequested: boolean;
  /** Q key: put the weapon away / draw it again. An empty hand is what lets you
   *  pickpocket, and it reads as harmless to anyone watching. */
  sheatheRequested: boolean;
  /** L key: re-lock an adjacent door/container you hold the key for. */
  relockRequested: boolean;
  /** X key: toggle take-cover against an adjacent wall/half wall/cubicle. */
  coverToggleRequested: boolean;
  /** Esc key: toggle the controls/help overlay ("esc menu"). */
  helpToggleRequested: boolean;
  /** N key: toggle night vision (requires the equipped device). */
  nightVisionToggleRequested: boolean;
  /** I key: toggle the inventory window. */
  inventoryToggleRequested: boolean;
  /** Arrow left/right: turn (screen-space). -1 = CCW, +1 = CW. */
  turnRequested: -1 | 0 | 1;
  /** Arrow up/down: limb cycle when focused. -1 = up, +1 = down. */
  verticalRequested: -1 | 0 | 1;
}

export function createInput(): InputState {
  return {
    held: new Set<string>(),
    debugToggleRequested: false,
    crouchLatched: false,
    focusToggleRequested: false,
    cyclePrevRequested: false,
    cycleNextRequested: false,
    weaponSwitchRequested: 0,
    markRequested: false,
    interactRequested: false,
    descendRequested: false,
    throwRequested: false,
    uniformRequested: false,
    sheatheRequested: false,
    relockRequested: false,
    coverToggleRequested: false,
    helpToggleRequested: false,
    nightVisionToggleRequested: false,
    inventoryToggleRequested: false,
    turnRequested: 0,
    verticalRequested: 0,
  };
}

/** Report a key transition. `down=true` on keydown, `false` on keyup. */
export function setKey(input: InputState, code: string, down: boolean): void {
  if (down) {
    if (input.held.has(code)) return;
    input.held.add(code);
    if (code === 'Backquote') input.debugToggleRequested = true;
    if (code === 'KeyC') input.crouchLatched = !input.crouchLatched;
    if (code === 'KeyF') input.focusToggleRequested = true;
    if (code === 'KeyG') input.cyclePrevRequested = true;
    if (code === 'KeyV') input.cycleNextRequested = true;
    if (code === 'Digit1') input.weaponSwitchRequested = 1;
    else if (code === 'Digit2') input.weaponSwitchRequested = 2;
    else if (code === 'Digit3') input.weaponSwitchRequested = 3;
    else if (code === 'Digit4') input.weaponSwitchRequested = 4;
    if (code === 'KeyM') input.markRequested = true;
    if (code === 'KeyE') input.interactRequested = true;
    if (code === 'KeyQ') input.descendRequested = true;
    if (code === 'KeyB') input.throwRequested = true;
    if (code === 'KeyU') input.uniformRequested = true;
    if (code === 'KeyQ') input.sheatheRequested = true;
    if (code === 'KeyL') input.relockRequested = true;
    if (code === 'KeyX') input.coverToggleRequested = true;
    // Esc opens the controls/help "menu" (the MOVE dev panel now lives behind the
    // debug toggle instead).
    if (code === 'Escape') input.helpToggleRequested = true;
    if (code === 'KeyN') input.nightVisionToggleRequested = true;
    // Inventory: the physical `I` key (one key per function). Unlike the other
    // mnemonics it is NOT letter-remapped — on Turkish-Q the letter 'i' sits at
    // the apostrophe while the labelled I key types 'ı'; we keep the labelled I.
    // The INV taskbar button is the layout-independent alternative.
    if (code === 'KeyI') input.inventoryToggleRequested = true;
    if (code === 'ArrowLeft') input.turnRequested = -1;
    else if (code === 'ArrowRight') input.turnRequested = 1;
    if (code === 'ArrowUp') input.verticalRequested = -1;
    else if (code === 'ArrowDown') input.verticalRequested = 1;
  } else {
    input.held.delete(code);
  }
}

export function consumeDebugToggle(input: InputState): boolean {
  const req = input.debugToggleRequested;
  input.debugToggleRequested = false;
  return req;
}

export function consumeFocusToggle(input: InputState): boolean {
  const r = input.focusToggleRequested;
  input.focusToggleRequested = false;
  return r;
}

export function consumeCyclePrev(input: InputState): boolean {
  const r = input.cyclePrevRequested;
  input.cyclePrevRequested = false;
  return r;
}

export function consumeCycleNext(input: InputState): boolean {
  const r = input.cycleNextRequested;
  input.cycleNextRequested = false;
  return r;
}

export function consumeWeaponSwitch(input: InputState): 0 | 1 | 2 | 3 | 4 {
  const r = input.weaponSwitchRequested;
  input.weaponSwitchRequested = 0;
  return r;
}

export function consumeMark(input: InputState): boolean {
  const r = input.markRequested;
  input.markRequested = false;
  return r;
}

export function consumeInteract(input: InputState): boolean {
  const r = input.interactRequested;
  input.interactRequested = false;
  return r;
}

export function consumeDescend(input: InputState): boolean {
  const r = input.descendRequested;
  input.descendRequested = false;
  return r;
}

export function consumeThrow(input: InputState): boolean {
  const r = input.throwRequested;
  input.throwRequested = false;
  return r;
}

export function consumeSheathe(input: InputState): boolean {
  const r = input.sheatheRequested;
  input.sheatheRequested = false;
  return r;
}

export function consumeUniform(input: InputState): boolean {
  const r = input.uniformRequested;
  input.uniformRequested = false;
  return r;
}

export function consumeRelock(input: InputState): boolean {
  const r = input.relockRequested;
  input.relockRequested = false;
  return r;
}

export function consumeCoverToggle(input: InputState): boolean {
  const r = input.coverToggleRequested;
  input.coverToggleRequested = false;
  return r;
}

export function consumeHelpToggle(input: InputState): boolean {
  const r = input.helpToggleRequested;
  input.helpToggleRequested = false;
  return r;
}

export function consumeNightVisionToggle(input: InputState): boolean {
  const r = input.nightVisionToggleRequested;
  input.nightVisionToggleRequested = false;
  return r;
}

export function consumeInventoryToggle(input: InputState): boolean {
  const r = input.inventoryToggleRequested;
  input.inventoryToggleRequested = false;
  return r;
}

/** True while any arrow key is held — used to detect "peeking" (aiming) in cover. */
export function isAiming(input: InputState): boolean {
  return (
    input.held.has('ArrowUp') ||
    input.held.has('ArrowDown') ||
    input.held.has('ArrowLeft') ||
    input.held.has('ArrowRight')
  );
}

export function consumeTurn(input: InputState): -1 | 0 | 1 {
  const r = input.turnRequested;
  input.turnRequested = 0;
  return r;
}

export function consumeVertical(input: InputState): -1 | 0 | 1 {
  const r = input.verticalRequested;
  input.verticalRequested = 0;
  return r;
}

/** True when Space is currently held; combat fire logic gates on this + weapon cooldown. */
export function isFireHeld(input: InputState): boolean {
  return input.held.has('Space');
}

/**
 * Screen-space move intent from WASD. W = screen-up (forward, toward where the
 * player is aiming), S = screen-down (back), A = screen-left (strafe-left), D =
 * screen-right (strafe-right); two adjacent keys give a diagonal (WA/WD/SA/SD),
 * and A or D alone strafes straight sideways. The caller rotates (sx, sy) into
 * world space with `screenToWorldByHeading` using the current camera rotation,
 * so movement is relative to the aim-up camera. Movement does NOT change facing
 * — the arrow keys aim (see `getFaceDir`).
 */
export function getMoveDir(input: InputState): { dx: number; dy: number } {
  let dx = 0;
  let dy = 0;
  if (input.held.has('KeyW')) dy -= 1;
  if (input.held.has('KeyS')) dy += 1;
  if (input.held.has('KeyA')) dx -= 1;
  if (input.held.has('KeyD')) dx += 1;
  return { dx, dy };
}

/**
 * LEG movement direction from WASD, world-absolute 8-way (W = north/up).
 * This is the new movement model: the legs point where WASD points, in world
 * space — NO camera rotation is applied (the legs are the reference frame; the
 * arrow keys aim the torso relative to them). Two adjacent keys give a diagonal;
 * opposing keys cancel. Returns {0,0} when no move key is held.
 */
export function legDirFromInput(input: InputState): { dx: number; dy: number } {
  let dx = 0;
  let dy = 0;
  if (input.held.has('KeyW')) dy -= 1;
  if (input.held.has('KeyS')) dy += 1;
  if (input.held.has('KeyA')) dx -= 1;
  if (input.held.has('KeyD')) dx += 1;
  return { dx: Math.sign(dx), dy: Math.sign(dy) };
}

/**
 * TORSO aim offset from the arrow keys, read as a screen "cross" 8-way vector
 * (Up = (0,-1) = forward, Down = (0,1) = behind, Left = (-1,0), Right = (1,0);
 * two adjacent arrows give a diagonal). This is a leg-RELATIVE offset — see
 * `composeTorsoFacing`. Held (not edge): the torso holds its look while the
 * arrows are down. Returns {0,0} when no arrow is held (torso snaps back to
 * the legs).
 */
export function torsoOffsetFromArrows(input: InputState): { dx: number; dy: number } {
  let dx = 0;
  let dy = 0;
  if (input.held.has('ArrowUp')) dy -= 1;
  if (input.held.has('ArrowDown')) dy += 1;
  if (input.held.has('ArrowLeft')) dx -= 1;
  if (input.held.has('ArrowRight')) dx += 1;
  return { dx: Math.sign(dx), dy: Math.sign(dy) };
}

/** 8-way headings in CW screen order starting from north (y-down). Index 0 = N,
 *  1 = NE, 2 = E, 3 = SE, 4 = S, 5 = SW, 6 = W, 7 = NW. */
export const DIR8: ReadonlyArray<readonly [number, number]> = [
  [0, -1], [1, -1], [1, 0], [1, 1],
  [0, 1], [-1, 1], [-1, 0], [-1, -1],
];

/** Index of an 8-way (dx, dy) in `DIR8`, or -1 if it isn't a unit 8-dir. */
export function dirIndex(dx: number, dy: number): number {
  for (let i = 0; i < DIR8.length; i++) {
    if (DIR8[i]![0] === dx && DIR8[i]![1] === dy) return i;
  }
  return -1;
}

/**
 * Compose the torso's absolute look direction from the legs' orientation and
 * the arrow-key offset (leg-relative). The offset is interpreted in a frame
 * where "up" (0,-1) means "same way the legs face": torsoIndex =
 * (legIndex + arrowIndex) % 8. So with legs facing SOUTH, Up→S, Left→E,
 * Right→W, Down→N. When no arrow is held, the torso faces the legs.
 */
export function composeTorsoFacing(
  legDx: number,
  legDy: number,
  arrowDx: number,
  arrowDy: number,
): { dx: number; dy: number } {
  const legIdx = Math.max(0, dirIndex(legDx, legDy)); // default north if legs are (0,0)
  const arrowIdx = dirIndex(arrowDx, arrowDy);
  if (arrowIdx < 0) return { dx: DIR8[legIdx]![0], dy: DIR8[legIdx]![1] };
  const torsoIdx = (legIdx + arrowIdx) % 8;
  return { dx: DIR8[torsoIdx]![0], dy: DIR8[torsoIdx]![1] };
}

/**
 * Convert screen-space input (sx, sy) to a world-space 8-way tile delta given
 * the camera's rotation in radians (Pixi CW positive). Inverse of the
 * world→screen rotation, then snap each axis to {-1, 0, 1}. At heading = 0 this
 * is identity (world = screen). As the aim-up camera rotates to the player's
 * facing, W stays "forward on screen".
 */
export function screenToWorldByHeading(
  sx: number,
  sy: number,
  heading: number,
): { dx: number; dy: number } {
  if (sx === 0 && sy === 0) return { dx: 0, dy: 0 };
  const c = Math.cos(heading);
  const s = Math.sin(heading);
  const wx = c * sx + s * sy;
  const wy = -s * sx + c * sy;
  return { dx: snapAxis(wx), dy: snapAxis(wy) };
}

function snapAxis(v: number): number {
  const r = Math.round(v);
  if (r > 0) return 1;
  if (r < 0) return -1;
  return 0;
}

/**
 * Currently-held turn direction from ArrowLeft/Right — -1 = CCW (turn left),
 * +1 = CW (turn right), 0 = neither or both. Drives hold-to-repeat relative
 * turning in the tick loop. The turn is RELATIVE to the player's current
 * facing (see `rotateFacing`), not an absolute compass direction.
 */
export function getTurnHeld(input: InputState): -1 | 0 | 1 {
  const l = input.held.has('ArrowLeft');
  const r = input.held.has('ArrowRight');
  if (l && !r) return -1;
  if (r && !l) return 1;
  return 0;
}

/** Rotate an 8-way facing by 45° relative to itself (CCW = -1, CW = +1). */
export function rotateFacing(
  facingDx: number,
  facingDy: number,
  dir: -1 | 1,
): { dx: number; dy: number } {
  // 8-way headings in CW screen order starting from north (y-down).
  const dirs: Array<[number, number]> = [
    [0, -1], [1, -1], [1, 0], [1, 1],
    [0, 1], [-1, 1], [-1, 0], [-1, -1],
  ];
  let idx = 0;
  for (let i = 0; i < dirs.length; i++) {
    const [ddx, ddy] = dirs[i]!;
    if (ddx === facingDx && ddy === facingDy) { idx = i; break; }
  }
  const next = (idx + (dir === 1 ? 1 : dirs.length - 1)) % dirs.length;
  const [ndx, ndy] = dirs[next]!;
  return { dx: ndx, dy: ndy };
}

/**
 * Grid-snapping arrow turn (CCW = -1, CW = +1). Turning always lands on a
 * CARDINAL (non-diagonal) heading:
 *   - From a cardinal → 90° to the next cardinal (skip the diagonal between).
 *   - From a diagonal → 45° to the adjacent cardinal, i.e. it ALIGNS back onto
 *     the grid rather than stepping past it.
 * So the arrows never leave you on a diagonal — a diagonal facing only happens
 * while you're actually moving diagonally (movement auto-follow).
 */
export function turnFacing(
  facingDx: number,
  facingDy: number,
  dir: -1 | 1,
): { dx: number; dy: number } {
  const isDiagonal = facingDx !== 0 && facingDy !== 0;
  const steps = isDiagonal ? 1 : 2; // 45° to align off a diagonal, else 90°
  let dx = facingDx;
  let dy = facingDy;
  for (let i = 0; i < steps; i++) {
    const r = rotateFacing(dx, dy, dir);
    dx = r.dx;
    dy = r.dy;
  }
  return { dx, dy };
}

/** Stance derived from modifier keys and the C toggle. Crouch beats run. */
export function getStance(input: InputState): Stance {
  if (
    input.crouchLatched ||
    input.held.has('ControlLeft') ||
    input.held.has('ControlRight')
  ) {
    return 'crouch';
  }
  if (input.held.has('ShiftLeft') || input.held.has('ShiftRight')) return 'run';
  return 'walk';
}
